import { getStore, updateStore } from 'core_main/store';
import { PublicCallback } from 'globals/types';
import { handleError } from 'globals/errors';
import Pubnub from 'pubnub';
import { backoffAuthCall } from 'core_main/api/pubnub';
import { handleCustomerDeletedEvent } from 'core_main/pubnub/events/customerDeleted';
import { handleChatSessionDeletedEvent } from 'core_main/pubnub/events/chatSessionDelete';
import {
  clearPubNub,
  getConversationPubNub,
  getMetaPubNub,
} from './helpers/instance';
import {
  KUSTOMER_APP_CHAT_CSAT_SEND_EVENT,
  KUSTOMER_APP_CHAT_MESSAGE_RECEIVE_EVENT,
  KUSTOMER_APP_CHAT_MESSAGE_SEND_EVENT,
  KUSTOMER_APP_CHAT_SESSION_DELETE_EVENT,
  KUSTOMER_APP_CHAT_SESSION_END_EVENT,
  KUSTOMER_APP_CHAT_SESSION_MERGED_EVENT,
  KUSTOMER_APP_CHAT_SESSION_UPDATE_EVENT,
  KUSTOMER_APP_CUSTOMER_DELETED,
  KUSTOMER_ASSISTANT_CHAT_SESSION_END_EVENT,
  PUBNUB_SUBSCRIBE_OPERATION,
  USER_TYPING,
  USER_TYPING_ENDED,
  AI_ASSISTANT_USER_TYPING,
} from './constants';
import { handlePubNubConversationSubscribeOperation } from './events/conversationSubscribeOperation';
import {
  createConversationChannelGroupString,
  createMetaCustomerChannelString,
} from './helpers/channelName';

import { InitializePubNubParameters } from './types';
import { handleChatMessageReceivedEvent } from './events/chatMessageReceived';
import { handleChatSessionUpdateEvent } from './events/chatSessionUpdate';
import { handleChatSessionMergedEvent } from './events/chatSessionMerged';
import { handleChatSessionEndedEvent } from './events/chatSessionEnded';
import { handleAssistantSessionEndedEvent } from './events/assistantSessionEnded';
import { handleChatCsatSendEvent } from './events/chatCsatSend';
import { handleUserTypingEvent } from './events/userTyping';
import { createPubnubNetworkStatusEventHandler } from './events/networkStatus';
import {
  setupGrantTokenRefresh,
  teardownGrantTokenRefresh,
} from './helpers/grantToken';

export {
  getConversationPubNub,
  getMetaPubNub,
  clearPubNub,
  stopPubNub,
} from './helpers/instance';

export const createPubNubMetaListeners = (outboundChatEnabled) => ({
  message: (messageEvent: Pubnub.MessageEvent) => {
    const { message } = messageEvent;

    const { name } = message;

    try {
      switch (name) {
        case KUSTOMER_APP_CHAT_SESSION_END_EVENT:
          handleChatSessionEndedEvent(message);
          break;
        case KUSTOMER_ASSISTANT_CHAT_SESSION_END_EVENT:
          handleAssistantSessionEndedEvent(message);
          break;
        case KUSTOMER_APP_CHAT_SESSION_UPDATE_EVENT:
          handleChatSessionUpdateEvent(message);
          break;
        case KUSTOMER_APP_CHAT_SESSION_MERGED_EVENT:
          handleChatSessionMergedEvent(message);
          break;
        case KUSTOMER_APP_CUSTOMER_DELETED:
          handleCustomerDeletedEvent();
          break;
        case KUSTOMER_APP_CHAT_SESSION_DELETE_EVENT:
          handleChatSessionDeletedEvent(message);
          break;
        default:
          break;
      }
    } catch (e) {
      handleError(e);
    }
  },
  signal: (signalEvent) => {
    const { message } = signalEvent;

    switch (message) {
      case USER_TYPING:
      case AI_ASSISTANT_USER_TYPING:
      case USER_TYPING_ENDED: {
        handleUserTypingEvent(signalEvent, {
          isAIGeneratingAnswer: message === AI_ASSISTANT_USER_TYPING,
        });
        break;
      }
      default:
        break;
    }
  },
  status: createPubnubNetworkStatusEventHandler(outboundChatEnabled),
});

export const createPubNubConversationListeners = (
  customerId: string,
  orgId: string,
  cb?: PublicCallback,
) => ({
  message: (messageEvent: Pubnub.MessageEvent) => {
    const { message, timetoken } = messageEvent;

    const { name } = message;

    try {
      switch (name) {
        case KUSTOMER_APP_CHAT_MESSAGE_RECEIVE_EVENT:
          handleChatMessageReceivedEvent(messageEvent);
          break;
        case KUSTOMER_APP_CHAT_MESSAGE_SEND_EVENT:
          handleChatMessageReceivedEvent(messageEvent);
          break;
        case KUSTOMER_APP_CHAT_CSAT_SEND_EVENT:
          handleChatCsatSendEvent(message, timetoken);
          break;
        default:
          break;
      }
    } catch (e) {
      handleError(e);
    }
  },
  status: (statusEvent: Pubnub.StatusEvent) => {
    const { operation } = statusEvent;

    if (operation === PUBNUB_SUBSCRIBE_OPERATION) {
      handlePubNubConversationSubscribeOperation(
        statusEvent,
        orgId,
        customerId,
        cb,
      );
    }
  },
});

export const initializePubNub = ({
  customerId,
  orgId,
  cb,
}: InitializePubNubParameters) => {
  const store = getStore();

  const { settings } = store;

  const { outboundChatEnabled } = settings;

  if (!customerId || !orgId || store.isPubNubConnected) {
    if (typeof cb === 'function') cb();
    return Promise.resolve();
  }

  teardownGrantTokenRefresh();
  clearPubNub();

  return setupGrantTokenRefresh(outboundChatEnabled)
    .catch(() => {
      // if an unrecoverable error occurs, try v1 auth
      return backoffAuthCall({ version: 1, outboundChatEnabled: false });
    })
    .then((authResponse) => {
      if (!authResponse) {
        if (typeof cb === 'function') cb();
        return;
      }

      getMetaPubNub()?.instance?.addListener(
        createPubNubMetaListeners(outboundChatEnabled),
      );

      getConversationPubNub()?.instance?.addListener(
        createPubNubConversationListeners(customerId, orgId, cb),
      );

      updateStore('isPubNubConnected', true);

      const channelGroupString = createConversationChannelGroupString(
        customerId,
        orgId,
      );
      const customerMetaChannel = createMetaCustomerChannelString(
        customerId,
        orgId,
      );

      getConversationPubNub()?.instance?.subscribe({
        channelGroups: [channelGroupString],
      });
      getMetaPubNub()?.instance?.subscribe({
        channels: [customerMetaChannel],
      });
    });
};
